/**
 * <ry-dropdown>
 *
 * Dropdown menu with click-outside handling.
 *
 * Minimal markup (recommended):
 * <ry-dropdown>
 *   <ry-button slot="trigger">Options</ry-button>
 *   <ry-menu>
 *     <ry-menu-item>Edit</ry-menu-item>
 *   </ry-menu>
 * </ry-dropdown>
 *
 * JS uses data-ry-target for queries, CSS uses classes for styling.
 */

import { RyElement } from '../core/ry-element.js';

export class RyDropdown extends RyElement {
  setup(): void {
    // Handle Option B: transform minimal markup
    this.#transformMinimalMarkup();

    const trigger = this.$<HTMLElement>('[data-ry-target="trigger"]');

    if (trigger) {
      this.on(trigger, 'click', () => this.toggle());
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
    }

    // Click outside to close
    this.on(document, 'click', this.#handleClickOutside);

    // Escape to close
    this.on(document, 'keydown', this.#handleKeydown);

    // Menu item clicks
    this.$$('[data-ry-target="item"], ry-menu-item').forEach(item => {
      this.on(item, 'click', () => this.close());
    });

    // Initialize state
    this.state = 'closed';
  }

  #transformMinimalMarkup(): void {
    // Handle ry-button with slot="trigger"
    // data-ry-target for JS queries, class for CSS styling
    const triggerButton = this.$('[slot="trigger"], ry-button:first-of-type');
    if (triggerButton && !this.$('[data-ry-target="trigger"]')) {
      triggerButton.setAttribute('data-ry-target', 'trigger');
    }

    // Handle ry-menu
    const menu = this.$('ry-menu');
    if (menu && !menu.hasAttribute('data-ry-target')) {
      menu.setAttribute('data-ry-target', 'menu');
      menu.classList.add('ry-dropdown__menu');
      menu.setAttribute('role', 'menu');
    }

    // Handle ry-menu-item
    this.$$('ry-menu-item').forEach(item => {
      if (!item.hasAttribute('data-ry-target')) {
        item.setAttribute('data-ry-target', 'item');
        item.classList.add('ry-dropdown__item');
        item.setAttribute('role', 'menuitem');
      }
    });
  }

  #handleClickOutside = (event: MouseEvent): void => {
    const target = event.target as Node;
    if (this.state === 'open' && !this.contains(target)) {
      this.close();
    }
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.state === 'open') {
      this.close();
      this.$<HTMLElement>('[data-ry-target="trigger"]')?.focus();
    }

    // Arrow key navigation within menu
    if (this.state === 'open' && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      const items = this.$$<HTMLElement>('[data-ry-target="item"], ry-menu-item');
      const currentIndex = items.indexOf(document.activeElement as HTMLElement);

      let newIndex: number;
      if (event.key === 'ArrowDown') {
        newIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
      } else {
        newIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
      }

      items[newIndex]?.focus();
    }
  };

  open(): void {
    if (this.state === 'open') return;

    const trigger = this.$('[data-ry-target="trigger"]');
    trigger?.setAttribute('aria-expanded', 'true');

    this.state = 'open';

    // Focus first item
    this.nextFrame().then(() => {
      this.$<HTMLElement>('[data-ry-target="item"], ry-menu-item')?.focus();
    });

    this.emit('open');
  }

  close(): void {
    if (this.state === 'closed') return;

    const trigger = this.$('[data-ry-target="trigger"]');
    trigger?.setAttribute('aria-expanded', 'false');

    this.state = 'closed';
    this.emit('close');
  }

  toggle(): void {
    if (this.state === 'open') {
      this.close();
    } else {
      this.open();
    }
  }
}

customElements.define('ry-dropdown', RyDropdown);

// Register placeholders for minimal markup
if (!customElements.get('ry-menu')) {
  customElements.define('ry-menu', class extends HTMLElement {});
}
if (!customElements.get('ry-menu-item')) {
  customElements.define('ry-menu-item', class extends HTMLElement {});
}
